'use client';

import { useEffect, useState } from 'react';

type NetworkInformation = {
  saveData?: boolean;
  addEventListener?: (type: string, listener: () => void) => void;
  removeEventListener?: (type: string, listener: () => void) => void;
};

/**
 * True when the visitor explicitly turned on "Data Saver".
 *
 * This deliberately ignores `effectiveType`/`downlink`: Chrome's throughput
 * estimate can be stale on a fresh page load (we measured it reporting "3g" on
 * a fast connection and flipping to "4g" seconds later), which would make the
 * hero swap files mid-flight and download twice. Switching by real bandwidth is
 * what adaptive streaming (HLS/DASH) is for — see the hero video notes.
 *
 * The Network Information API is Chromium-only; elsewhere this stays `false`
 * and the caller keeps its default behaviour.
 */
export function usePrefersLightMedia(): boolean {
  const [prefersLight, setPrefersLight] = useState(false);

  useEffect(() => {
    const connection = (navigator as Navigator & { connection?: NetworkInformation }).connection;
    if (!connection) return;

    const update = () => setPrefersLight(Boolean(connection.saveData));

    update();
    connection.addEventListener?.('change', update);
    return () => connection.removeEventListener?.('change', update);
  }, []);

  return prefersLight;
}
